<script setup>
import { computed } from 'vue';
import { useStore } from 'dashboard/composables/store';

const props = defineProps({
  funnels: { type: Array, default: () => [] },
});
defineEmits(['remove']);

const action = defineModel('action', { type: Object, required: true });

const store = useStore();

const teams = computed(() => store.getters['teams/getTeams']);
const agents = computed(() => store.getters['agents/getAgents']);

// Só caixas Cloud API têm template aprovado pela Meta.
const cloudInboxes = computed(() =>
  (store.getters['inboxes/getWhatsAppInboxes'] || []).filter(
    inbox => inbox.provider === 'whatsapp_cloud'
  )
);

const templatesOfInbox = computed(() => {
  const inbox = cloudInboxes.value.find(i => i.id === action.value.inbox_id);
  return (inbox?.message_templates || []).filter(
    template => (template.status || '').toUpperCase() === 'APPROVED'
  );
});

const targetStages = computed(
  () =>
    props.funnels.find(funnel => funnel.id === action.value.target_funnel_id)
      ?.stages || []
);

const hasTarget = computed(() =>
  ['move', 'duplicate'].includes(action.value.type)
);

// Selects herdam o estilo global (_base.scss), que desenha a seta do
// dropdown como background contando com o padding-right dele. Sobrescrever o
// padding aqui escondia a seta.
const selectClass = 'w-full mb-0';
const inputClass =
  'w-full h-10 px-3 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12';
</script>

<template>
  <div class="flex flex-col gap-3 p-3 border rounded-lg border-n-weak">
    <div class="flex items-center justify-between">
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t(`AUTOMATION.CRM.FORM.ACTION_${action.type.toUpperCase()}`) }}
      </span>
      <button
        type="button"
        class="text-sm text-n-ruby-11"
        @click="$emit('remove')"
      >
        {{ $t('AUTOMATION.CRM.FORM.REMOVE') }}
      </button>
    </div>

    <template v-if="hasTarget">
      <select v-model="action.target_funnel_id" :class="selectClass">
        <option :value="null">
          {{ $t('AUTOMATION.CRM.FORM.PICK_FUNNEL') }}
        </option>
        <option v-for="funnel in funnels" :key="funnel.id" :value="funnel.id">
          {{ funnel.name }}
        </option>
      </select>

      <select v-model="action.target_stage_id" :class="selectClass">
        <option :value="null">
          {{ $t('AUTOMATION.CRM.FORM.FIRST_STAGE') }}
        </option>
        <option v-for="stage in targetStages" :key="stage.id" :value="stage.id">
          {{ stage.name }}
        </option>
      </select>
    </template>

    <template v-if="action.type === 'duplicate'">
      <label class="flex items-center gap-2 text-sm text-n-slate-12">
        <input v-model="action.copy.value_and_products" type="checkbox" />
        {{ $t('AUTOMATION.CRM.FORM.COPY_VALUE') }}
      </label>
      <label class="flex items-center gap-2 text-sm text-n-slate-12">
        <input v-model="action.copy.installments_and_payment" type="checkbox" />
        {{ $t('AUTOMATION.CRM.FORM.COPY_INSTALLMENTS') }}
      </label>
      <label class="flex items-center gap-2 text-sm text-n-slate-12">
        <input v-model="action.copy.open_tasks" type="checkbox" />
        {{ $t('AUTOMATION.CRM.FORM.COPY_TASKS') }}
      </label>
    </template>

    <template v-if="action.type === 'create_task'">
      <input
        v-model="action.title"
        type="text"
        :class="inputClass"
        :placeholder="$t('AUTOMATION.CRM.FORM.TASK_TITLE')"
      />
      <textarea
        v-model="action.description"
        :class="inputClass"
        :placeholder="$t('AUTOMATION.CRM.FORM.TASK_DESCRIPTION')"
      />
      <input
        v-model.number="action.due_in_days"
        type="number"
        min="0"
        :class="inputClass"
        :placeholder="$t('AUTOMATION.CRM.FORM.TASK_DUE_IN_DAYS')"
      />
    </template>

    <template v-if="action.type === 'notify'">
      <select v-model="action.target.mode" :class="selectClass">
        <option value="deal_assignee">
          {{ $t('AUTOMATION.CRM.FORM.NOTIFY_ASSIGNEE') }}
        </option>
        <option value="team">
          {{ $t('AUTOMATION.CRM.FORM.NOTIFY_TEAM') }}
        </option>
        <option value="user">
          {{ $t('AUTOMATION.CRM.FORM.NOTIFY_USER') }}
        </option>
      </select>
      <select
        v-if="action.target.mode === 'team'"
        v-model="action.target.team_id"
        :class="selectClass"
      >
        <option v-for="team in teams" :key="team.id" :value="team.id">
          {{ team.name }}
        </option>
      </select>
      <select
        v-if="action.target.mode === 'user'"
        v-model="action.target.user_id"
        :class="selectClass"
      >
        <option v-for="agent in agents" :key="agent.id" :value="agent.id">
          {{ agent.name }}
        </option>
      </select>
    </template>

    <template v-if="action.type === 'send_template'">
      <select v-model="action.inbox_id" :class="selectClass">
        <option :value="null">
          {{ $t('AUTOMATION.CRM.FORM.PICK_INBOX') }}
        </option>
        <option v-for="inbox in cloudInboxes" :key="inbox.id" :value="inbox.id">
          {{ inbox.name }}
        </option>
      </select>
      <select v-model="action.template_name" :class="selectClass">
        <option value="">
          {{ $t('AUTOMATION.CRM.FORM.PICK_TEMPLATE') }}
        </option>
        <option
          v-for="template in templatesOfInbox"
          :key="`${template.name}-${template.language}`"
          :value="template.name"
        >
          {{ template.name }} ({{ template.language }})
        </option>
      </select>
      <p class="mb-0 text-xs text-n-slate-10">
        {{ $t('AUTOMATION.CRM.FORM.TEMPLATE_HINT') }}
      </p>
    </template>

    <template v-if="action.type === 'webhook'">
      <input
        v-model="action.url"
        type="url"
        :class="inputClass"
        :placeholder="$t('AUTOMATION.CRM.FORM.WEBHOOK_URL')"
      />
      <input
        v-model="action.secret"
        type="text"
        :class="inputClass"
        :placeholder="$t('AUTOMATION.CRM.FORM.WEBHOOK_SECRET')"
      />
    </template>

    <template v-if="hasTarget || action.type === 'create_task'">
      <select v-model="action.assignment.mode" :class="selectClass">
        <option value="keep">
          {{ $t('AUTOMATION.CRM.FORM.ASSIGN_KEEP') }}
        </option>
        <option value="team">
          {{ $t('AUTOMATION.CRM.FORM.ASSIGN_TEAM') }}
        </option>
        <option value="user">
          {{ $t('AUTOMATION.CRM.FORM.ASSIGN_USER') }}
        </option>
      </select>
      <select
        v-if="action.assignment.mode === 'team'"
        v-model="action.assignment.team_id"
        :class="selectClass"
      >
        <option v-for="team in teams" :key="team.id" :value="team.id">
          {{ team.name }}
        </option>
      </select>
      <select
        v-if="action.assignment.mode === 'user'"
        v-model="action.assignment.user_id"
        :class="selectClass"
      >
        <option v-for="agent in agents" :key="agent.id" :value="agent.id">
          {{ agent.name }}
        </option>
      </select>
    </template>
  </div>
</template>
